import { useState, useEffect, useRef } from "react";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/app/components/shadcn/accordion";
import { Input } from "@/app/components/shadcn/input";
import { FilterInputGroup } from "@/app/lib/features/filters/filters.types";
import useFilters from "@/app/lib/features/filters/filters.hook";
import useDebounce from "@/app/hooks/useDebounce";
import {
  getPriceBounds,
  isSamePriceRange,
  normalizePriceRange,
} from "@/app/utils/priceFilter.utils";

interface FilterPriceSectionProps {
  filter: FilterInputGroup;
}

// Ölçek viewport'a değil, input'un içinde bulunduğu kolonun genişliğine bağlı:
// aynı bileşen hem 288px'lik mobil çekmecede hem de dar sidebar kolonunda
// render oluyor ve bu iki genişlik viewport ile orantılı değil (telefon 430px
// de olsa çekmece hep 288px). Bu yüzden `md:` gibi breakpoint'ler değil,
// container query birimi cqi kullanılıyor — 1cqi = kapsayıcının %1 genişliği.
//
// Alt sınırlar iki kutu + 8px gap hesabından geliyor: kutu ≈ (100cqi - 8) / 2,
// yazı alanı ≈ 50cqi - 20. "Max (2250)" ~5.3em geldiği için font ≈ 9cqi - 4px
// tam oturuyor; 16px'te (Input'un varsayılanı) tavan yapıyor, dar kolonda
// 12px'e kadar iniyor.
const PRICE_INPUT_STYLE = {
  fontSize: "clamp(12px, calc(9cqi - 4px), 16px)",
  height: "clamp(34px, 13cqi, 48px)",
  paddingInline: "clamp(6px, 2.5cqi, 12px)",
} as const;

const FilterPriceSection = ({ filter }: FilterPriceSectionProps) => {
  const { selected, setPrice } = useFilters();
  const [minVal, setMinVal] = useState(selected.price.min?.toString() ?? "");
  const [maxVal, setMaxVal] = useState(selected.price.max?.toString() ?? "");

  const bounds = getPriceBounds(filter.min, filter.max);

  const debouncedMin = useDebounce(minVal, 600);
  const debouncedMax = useDebounce(maxVal, 600);

  // Redux'taki güncel aralık — effect'i selected'a bağlamadan okuyabilmek için.
  // Dep listesine eklenirse setPrice sonrası effect yeniden çalışır.
  const selectedPriceRef = useRef(selected.price);
  useEffect(() => {
    selectedPriceRef.current = selected.price;
  }, [selected.price]);

  useEffect(() => {
    const range = normalizePriceRange(debouncedMin, debouncedMax, bounds);
    if (isSamePriceRange(range, selectedPriceRef.current)) return;
    setPrice(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMin, debouncedMax]);

  // Kırpılan/takas edilen değerler input'a geri yazılır. Önceden yalnızca
  // Redux güncelleniyordu: kullanıcı 5 yazınca filtre 29'a kırpılıyor ama
  // kutuda 5 görünmeye devam ediyordu. Yazma bitince (blur) düzeltiliyor —
  // her tuşta yapılsa "1500" yazmak imkânsız olurdu.
  const handleBlur = () => {
    const range = normalizePriceRange(minVal, maxVal, bounds);
    setMinVal(range.min !== undefined ? String(range.min) : "");
    setMaxVal(range.max !== undefined ? String(range.max) : "");
  };

  if (filter.min === 0 && filter.max === 0) return null;

  return (
    <AccordionItem value={filter.key}>
      <AccordionTrigger className="hover:bg-muted p-2 my-2">
        {filter.title}
        {filter.selected_count > 0 && (
          <span className="ml-auto mr-2 w-5 h-5 flex items-center justify-center text-xs bg-black text-white rounded-full shrink-0">
            {filter.selected_count}
          </span>
        )}
      </AccordionTrigger>
      <AccordionContent className="p-1 space-y-2">
        <p className="text-xs text-muted-foreground">{filter.description}</p>
        {/* containerType: cqi birimlerinin ölçüleceği kapsayıcı bu div.
            grid-cols-2: iki kutu içerikten bağımsız olarak hep eşit genişlikte. */}
        <div
          className="grid grid-cols-2 gap-2"
          style={{ containerType: "inline-size" }}
        >
          <Input
            placeholder={`Min (${bounds.min})`}
            type="number"
            inputMode="numeric"
            min={bounds.min}
            max={bounds.max}
            value={minVal}
            onChange={(e) => setMinVal(e.target.value)}
            onBlur={handleBlur}
            style={PRICE_INPUT_STYLE}
          />
          <Input
            placeholder={`Max (${bounds.max})`}
            type="number"
            inputMode="numeric"
            min={bounds.min}
            max={bounds.max}
            value={maxVal}
            onChange={(e) => setMaxVal(e.target.value)}
            onBlur={handleBlur}
            style={PRICE_INPUT_STYLE}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default FilterPriceSection;
